import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  Table,
  Typography,
  message,
  Tag,
  Space,
  Button,
  Spin,
  Input,
  Form,
  Modal,
  Radio,
  Select,
} from "antd";
import {
  CarOutlined,
  EyeOutlined,
  MergeCellsOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  getDuplicatedVehicles,
  mergeDuplicatedVehicles,
} from "../api/vehicle/duplicatedVehicleRequest";
import { countries, getCountryNameByCode } from "../utils/countries";
import { formatDateTime } from "../utils/constants";

const { Title, Text } = Typography;
const { Search } = Input;

export default function DuplicatedVehiclesPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const response = await getDuplicatedVehicles();
      setDuplicates(response.data || []);
    } catch (error) {
      console.error("Error fetching duplicated vehicles:", error);
      message.error("Erreur lors du chargement des véhicules dupliqués");
    } finally {
      setLoading(false);
    }
  };

  const selectPrimaryVehicle = (vehicles) => {
    const withOwner = vehicles.find((v) => Boolean(v.owner_id));
    if (withOwner) return withOwner;

    return [...vehicles].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )[0];
  };

  const openMergeModal = (group) => {
    if (!group?.merge_allowed) {
      message.warning(group?.merge_block_reason || "Fusion desactivee pour ce groupe");
      return;
    }

    const primary = selectPrimaryVehicle(group.vehicles);
    const secondary = group.vehicles.find((v) => v.id !== primary.id);

    setSelectedGroup(group);
    setMergeModalOpen(true);

    form.setFieldsValue({
      master_vehicle_id: primary.id,
      country: primary.country || secondary?.country || "MA",
      brand: primary.brand || secondary?.brand || "",
      model: primary.model || secondary?.model || "",
    });
  };

  const closeMergeModal = () => {
    setMergeModalOpen(false);
    setSelectedGroup(null);
    form.resetFields();
  };

  const selectedMasterId = Form.useWatch("master_vehicle_id", form);
  const selectedMaster = selectedGroup?.vehicles?.find(
    (vehicle) => vehicle.id === selectedMasterId
  );
  const selectedDuplicates = selectedGroup?.vehicles?.filter(
    (vehicle) => vehicle.id !== selectedMasterId
  ) || [];

  const onMasterChange = (event) => {
    const masterId = event.target.value;
    const nextMaster = selectedGroup?.vehicles?.find((v) => v.id === masterId);
    const nextDuplicate = selectedGroup?.vehicles?.find((v) => v.id !== masterId);

    form.setFieldsValue({
      country: nextMaster?.country || nextDuplicate?.country || "MA",
      brand: nextMaster?.brand || nextDuplicate?.brand || "",
      model: nextMaster?.model || nextDuplicate?.model || "",
    });
  };

  const handleMerge = async () => {
    if (!selectedGroup || !selectedGroup.merge_allowed) return;

    try {
      const values = await form.validateFields();
      const duplicateVehicles = selectedGroup.vehicles.filter(
        (vehicle) => vehicle.id !== values.master_vehicle_id
      );

      if (!duplicateVehicles.length) {
        message.error("Véhicule secondaire introuvable");
        return;
      }

      setMerging(true);
      const payload = {
        master_vehicle_id: values.master_vehicle_id,
        country: values.country,
        brand: values.brand || null,
        model: values.model || null,
      };

      if ((selectedGroup?.count || 0) === 2) {
        payload.duplicate_vehicle_id = duplicateVehicles[0].id;
      }

      await mergeDuplicatedVehicles(payload);

      message.success("Fusion effectuée avec succès");
      closeMergeModal();
      fetchDuplicates();
    } catch (error) {
      const backendError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Erreur lors de la fusion";

      const firstError =
        typeof backendError === "string"
          ? backendError
          : Object.values(backendError)?.[0]?.[0] || "Erreur lors de la fusion";

      if (firstError !== "outOfDate") {
        message.error(firstError);
      }
    } finally {
      setMerging(false);
    }
  };

  const vehicleColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
      width: 150,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Pays",
      dataIndex: "country",
      key: "country",
      width: 120,
      render: (country) => (
        <Tag color={country === "other" ? "default" : "blue"}>
          {getCountryNameByCode(country) || country || "Autre"}
        </Tag>
      ),
    },
    {
      title: "Propriétaire",
      dataIndex: "owner_id",
      key: "owner_id",
      width: 120,
      render: (ownerId) =>
        ownerId ? (
          <Tag color="green">Assigné</Tag>
        ) : (
          <Tag color="default">Non assigné</Tag>
        ),
    },
    {
      title: "Infractions",
      dataIndex: "infraction_count",
      key: "infraction_count",
      width: 100,
      render: (count) => (
        <Tag color={count > 0 ? "red" : "default"}>{count}</Tag>
      ),
    },
    {
      title: "Avertissements",
      dataIndex: "warning_count",
      key: "warning_count",
      width: 120,
      render: (count) => (
        <Tag color={count > 0 ? "orange" : "default"}>{count || 0}</Tag>
      ),
    },
    {
      title: "Abonnements",
      dataIndex: "subscription_count",
      key: "subscription_count",
      width: 120,
      render: (count) => (
        <Tag color={count > 0 ? "purple" : "default"}>{count || 0}</Tag>
      ),
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (date) => formatDateTime(date),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="primary"
          ghost
          icon={<EyeOutlined />}
          size="small"
          onClick={() => window.open(`/vehicle/${record.id}`, "_blank")}
        >
          Voir
        </Button>
      ),
    },
  ];

  const mainColumns = [
    {
      title: "Matricule Normalisé",
      dataIndex: "matricule_normalized",
      key: "matricule_normalized",
      width: 200,
      render: (text) => (
        <Space>
          <WarningOutlined style={{ color: "#ff4d4f" }} />
          <Text strong style={{ fontSize: "16px" }}>
            {text.toUpperCase()}
          </Text>
        </Space>
      ),
    },
    {
      title: "Nombre de duplicatas",
      dataIndex: "count",
      key: "count",
      width: 150,
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: "descend",
      render: (count) => (
        <Tag color="red" style={{ fontSize: "14px", padding: "4px 12px" }}>
          {count} véhicules
        </Tag>
      ),
    },
    {
      title: "Total Infractions",
      key: "total_infractions",
      width: 150,
      sorter: (a, b) => {
        const totalA = a.vehicles.reduce(
          (sum, v) => sum + (v.infraction_count || 0),
          0
        );
        const totalB = b.vehicles.reduce(
          (sum, v) => sum + (v.infraction_count || 0),
          0
        );
        return totalA - totalB;
      },
      render: (_, record) => {
        const total = record.vehicles.reduce(
          (sum, v) => sum + (v.infraction_count || 0),
          0
        );
        return (
          <Tag color={total > 0 ? "orange" : "default"}>
            {total} infractions
          </Tag>
        );
      },
    },
    {
      title: "Fusion",
      key: "merge_action",
      width: 170,
      render: (_, record) => {
        if (!record.merge_allowed) {
          return <Tag color="gold">Fusion bloquee</Tag>;
        }

        return (
          <Button
            type="primary"
            icon={<MergeCellsOutlined />}
            onClick={() => openMergeModal(record)}
          >
            Fusionner
          </Button>
        );
      },
    },
  ];

  const filteredDuplicates = duplicates.filter((duplicate) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      duplicate.matricule_normalized.toLowerCase().includes(search) ||
      duplicate.vehicles.some((v) =>
        v.matricule.toLowerCase().includes(search)
      )
    );
  });

  return (
    <div className="p-6">
      <Card className="shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <CarOutlined style={{ fontSize: "24px", color: "#722ed1" }} />
            <Title level={2} className="m-0">
              Véhicules Dupliqués
            </Title>
          </div>
          <Button type="primary" onClick={fetchDuplicates} loading={loading}>
            Actualiser
          </Button>
        </div>

        <div className="mb-4">
          <Text type="secondary">
            Cette page affiche les véhicules ayant le même matricule normalisé
            mais avec des variations dans le format ou le pays.
          </Text>
        </div>

        <div className="mb-4">
          <Search
            placeholder="Rechercher par matricule..."
            allowClear
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 400 }}
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={mainColumns}
            dataSource={filteredDuplicates}
            rowKey="matricule_normalized"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              total: filteredDuplicates.length,
              showTotal: (total) => `Total: ${total} groupes de duplicatas`,
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div className="p-4 bg-gray-50 rounded">
                  {!record.merge_allowed && (
                    <Alert
                      className="mb-4"
                      type="warning"
                      showIcon
                      message="Fusion désactivée pour ce groupe"
                      description={record.merge_block_reason || "Fusion 2+ bloquée: ce groupe ne respecte pas les conditions (propriétaire, abonnement, documents)."}
                    />
                  )}
                  <Title level={5} className="mb-4">
                    Détails des véhicules dupliqués ({record.count})
                  </Title>
                  <Table
                    columns={vehicleColumns}
                    dataSource={record.vehicles}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 1000 }}
                    size="small"
                  />
                </div>
              ),
              expandIcon: ({ expanded, onExpand, record }) => (
                <Button
                  type="link"
                  onClick={(e) => onExpand(record, e)}
                  icon={expanded ? "▼" : "▶"}
                >
                  {expanded ? "Masquer" : "Afficher"} les détails
                </Button>
              ),
            }}
            locale={{
              emptyText: "Aucun véhicule dupliqué trouvé",
            }}
          />
        )}

        {!loading && filteredDuplicates.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded">
            <Text strong>
              <WarningOutlined className="mr-2" />
              Remarque:
            </Text>
            <Text>
              {" "}
              Les véhicules avec le même matricule normalisé peuvent avoir des
              formats différents (espaces, tirets) ou des pays différents.
              Vérifiez attentivement avant de fusionner ou supprimer.
            </Text>
          </div>
        )}

        <Modal
          title={selectedGroup?.count > 2 ? "Fusionner un groupe de véhicules" : "Fusionner deux profils véhicule"}
          open={mergeModalOpen}
          onCancel={closeMergeModal}
          onOk={handleMerge}
          okText="Confirmer la fusion"
          confirmLoading={merging}
          destroyOnClose
          width={760}
        >
          {selectedGroup && (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Alert
                type="info"
                showIcon
                message={`Matricule normalisé: ${selectedGroup.matricule_normalized?.toUpperCase()}`}
                description={selectedGroup?.count > 2
                  ? "Choisissez le profil principal. Tous les autres profils du groupe seront fusionnés dans celui-ci."
                  : "Choisissez le profil principal, puis définissez les valeurs finales (pays, marque, modèle) avant la fusion."}
              />

              <Form form={form} layout="vertical">
                <Form.Item
                  name="master_vehicle_id"
                  label="Profil principal (sera conservé)"
                  rules={[{ required: true, message: "Sélectionnez un profil principal" }]}
                >
                  <Radio.Group onChange={onMasterChange}>
                    <Space direction="vertical">
                      {selectedGroup.vehicles.map((vehicle) => (
                        <Radio value={vehicle.id} key={vehicle.id}>
                          ID {vehicle.id} - {vehicle.matricule} - {getCountryNameByCode(vehicle.country) || vehicle.country || "Autre"}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  name="country"
                  label="Pays final"
                  rules={[{ required: true, message: "Le pays est obligatoire" }]}
                >
                  <Select
                    showSearch
                    placeholder="Choisir un pays"
                    optionFilterProp="label"
                    options={countries.map((country) => ({
                      value: country.code,
                      label: country.name,
                    }))}
                  />
                </Form.Item>

                <Form.Item name="brand" label="Marque finale">
                  <Input placeholder="Ex: Dacia" />
                </Form.Item>

                <Form.Item name="model" label="Modèle final">
                  <Input placeholder="Ex: Logan" />
                </Form.Item>
              </Form>

              <Card size="small" title="Résumé de la fusion">
                <Space direction="vertical" size={2}>
                  <Text>
                    Profil conservé: ID {selectedMaster?.id || "-"} ({selectedMaster?.matricule || "-"})
                  </Text>
                  <Text>
                    Profils supprimés: {selectedDuplicates.length}
                  </Text>
                </Space>
              </Card>
            </Space>
          )}
        </Modal>
      </Card>
    </div>
  );
}
